import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { BookingService } from '../../services/booking.service';
import { Booking } from '../../../models/booking';
import { CustomerNameCellComponent } from '../../ag-grid-cells/customer-name-cell.component';
import { BookingItem } from '../../../models/booking-item';
import { dateComparator } from '../../helpers/DateComparator';
import { BookingNumberCellComponent } from '../../ag-grid-cells/booking-number-cell.component';
import { ColDef, FirstDataRenderedEvent, GridOptions } from 'ag-grid-community';
import { LocationNameFromIdPipe } from '../../pipes/location-name-from-id.pipe';
import { BookingLocationCellComponent } from '../../ag-grid-cells/booking-location-cell.component';
import { Subject } from 'rxjs';
import moment from 'moment';
import {
  CalendarUtils,
  CalendarMonthModule,
  CalendarWeekModule,
  CalendarDayModule,
  CalendarA11y,
  CalendarDateFormatter,
  CalendarEventTitleFormatter,
  DateAdapter,
  CalendarCommonModule
} from 'angular-calendar';
import {
  MatButtonToggleGroup,
  MatButtonToggle
} from '@angular/material/button-toggle';
import { Router } from '@angular/router';
import { DateFormatPipe } from 'ngx-moment';
import { ConfigService } from '../../services/config.service';
import { BookingItemNameCellComponent } from '../../ag-grid-cells/booking-item-name-cell.component';
import { MatIconButton, MatButton } from '@angular/material/button';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { NgIf, NgStyle, NgSwitch, NgSwitchCase } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { FlexModule } from '@angular/flex-layout';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';

@Component({
  selector: 'booking-list',
  templateUrl: './booking-list.component.html',
  styles: [
    `
      :host {
        display: block;
        min-height: 600px;
        width: 100%;
      }
    `
  ],
  providers: [
    BookingService,
    LocationNameFromIdPipe,
    CalendarUtils,
    DateFormatPipe,
    CalendarUtils,
    {
      provide: DateAdapter,
      useFactory: adapterFactory
    },
    CalendarA11y,
    CalendarDateFormatter,
    CalendarEventTitleFormatter
  ],
  standalone: true,
  imports: [
    FlexModule,
    MatButtonToggleGroup,
    MatButtonToggle,
    MatIcon,
    NgIf,
    AgGridModule,
    NgStyle,
    ExtendedModule,
    MatIconButton,
    MatButton,
    NgSwitch,
    NgSwitchCase,
    CalendarCommonModule,
    CalendarMonthModule,
    CalendarWeekModule,
    CalendarDayModule
  ]
})
export class BookingListComponent implements OnInit {
  @Input() set bookings(value: Booking[]) {
    if (value && value.length) {
      this._bookingItems =
        this.bookingService.bookingsToBookingListItems(value);
      this.calendarItems = this.bookingService.bookingsToCalendarItems(value);
      this.setNearestFutureBookingToCurrentDate();
      this.refresh.next();
      if (this.grid?.api) {
        this.grid.api.setRowData(this._bookingItems);
      }
    }
    if (value && value.length === 0) {
      this._bookingItems = [];
      this.calendarItems = [];
      this.refresh.next();
      if (this.grid?.api) {
        this.grid.api.setRowData(this._bookingItems);
      }
    }
  }

  @Input() set bookingItems(value: BookingItem[]) {
    if (value && value.length) {
      value.sort((a, b) => {
        return new Date(a.on_hire).getTime() - new Date(b.on_hire).getTime();
      });
      this._bookingItems = value;
      this.calendarItems = this.bookingService.bookingItemsToCalendarItems(
        value,
        []
      );
      this.refresh.next();
      if (this.grid?.api) {
        this.grid.api.setRowData(this._bookingItems);
      }
    }
  }

  @Input() set containingComponent(value) {
    if (value === 'customer') {
      this.columns = this.columns.filter(
        ({ headerName }) => headerName !== 'Customer'
      );
    }
  }

  @Input() height = 200;
  @ViewChild(MatButtonToggleGroup, { static: true })
  group: MatButtonToggleGroup;

  columns: ColDef[] = [
    {
      headerName: 'Booking',
      cellRenderer: BookingNumberCellComponent,
      width: 120,
      field: 'booking_id',
      resizable: true
    },
    {
      headerName: 'Customer',
      cellRenderer: CustomerNameCellComponent,
      resizable: true,
      field: 'booking.customer'
    },
    {
      headerName: 'On Hire',
      field: 'on_hire',
      resizable: true,
      width: 180,
      valueGetter: (params) => {
        return this.datePipe.transform(
          params.data['on_hire'],
          ConfigService.dates.shortDateTimeFormat
        );
      },
      comparator: dateComparator
    },
    {
      headerName: 'Off Hire',
      field: 'created',
      resizable: true,
      width: 180,
      valueGetter: (params) => {
        return this.datePipe.transform(
          params.data['off_hire'],
          ConfigService.dates.shortDateTimeFormat
        );
      },
      comparator: dateComparator
    },
    {
      headerName: 'Item',
      cellRenderer: BookingItemNameCellComponent,
      resizable: true,
      width: 240
    },
    {
      headerName: 'Col/Del',
      field: 'location_id',
      resizable: true,
      cellRenderer: BookingLocationCellComponent,
      width: 240
    },
    {
      headerName: 'Status',
      resizable: true,
      valueGetter: (params) => {
        return params.data.booking.status;
      }
    },
    {
      headerName: 'Purchase Order',
      resizable: true,
      filter: 'agTextColumnFilter',
      valueGetter: (params) => {
        return params.data.booking.purchase_order;
      }
    }
  ];

  calendarViewType = 'week';
  gridOptions: GridOptions = {
    rowSelection: 'single',
    headerHeight: 45,
    onGridReady: () => this.gridReady(),
    onFirstDataRendered: (event: FirstDataRenderedEvent<any>) => {
      setTimeout(() => this.grid?.api?.autoSizeColumns(this.columns), 600);
    }
  };

  _bookingItems: BookingItem[] = [];
  calendarItems: any[] = [];
  refresh = new Subject<void>();
  calendarViewDate = new Date();
  calendarViewDateMoment = moment();
  @ViewChild('agGrid') grid!: AgGridAngular;

  constructor(
    private bookingService: BookingService,
    private datePipe: DateFormatPipe,
    private router: Router
  ) {}

  ngOnInit() {
    this.group.writeValue('table');
  }

  gridReady() {
    this.grid.api.setGridOption('columnDefs', this.columns);
    this.grid.api.setGridOption('rowData', this._bookingItems);
  }

  bookingClicked(event) {
    this.router.navigate(['/bookings/view', event.event.item.booking_id]);
  }

  changeViewDate(amount) {
    if (amount === 0) {
      this.calendarViewDate = new Date();
      return;
    }
    this.calendarViewDate = moment(this.calendarViewDate)
      .add(amount, this.calendarViewType)
      .toDate();
    this.calendarViewDateMoment = moment(this.calendarViewDate);
    this.refresh.next();
  }

  changeCalendarView(newType: string) {
    if (newType === this.calendarViewType) {
      return;
    }

    this.calendarViewType = newType;
  }

  private setNearestFutureBookingToCurrentDate() {
    const now = new Date();
    let currentDate;
    this.calendarItems.forEach((item) => {
      if (!currentDate && item.start.getTime() > now.getTime()) {
        this.calendarViewDate = currentDate = item.start;
      }
    });
  }
}
